import { type PointerEvent, useCallback, useEffect, useRef, useState } from 'react';

const TOUCH = '(pointer: coarse)';
/** A press shorter than this is a tap: nothing is sent, the user gets a hint instead. */
export const MIN_HOLD_MS = 400;
/** Sliding the finger this far from the button arms cancel, as in messaging apps. */
export const CANCEL_DISTANCE_PX = 60;

export type PushToTalkHint = 'hold' | 'holdAgain' | null;

/** True on touch screens (phones, tablets): there the microphone works as push to talk. */
export function usePushToTalkMode(): boolean {
  const query = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(TOUCH).matches;
  const [touch, setTouch] = useState(query);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(TOUCH);
    const onChange = () => setTouch(media.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);
  return touch;
}

/**
 * Hold the microphone to record, release to convert to text, slide away to cancel. A release
 * before the microphone is ready (first permission prompt) or a quick tap records nothing.
 */
export function usePushToTalk(dictation: {
  start: () => Promise<boolean>;
  stop: () => void;
  cancel: () => void;
}) {
  const held = useRef(false);
  const startedAt = useRef(0);
  const origin = useRef({ x: 0, y: 0 });
  const [holding, setHolding] = useState(false);
  const [cancelArmed, setCancelArmed] = useState(false);
  const [hint, setHint] = useState<PushToTalkHint>(null);
  const latest = useRef(dictation);
  latest.current = dictation;

  const onPointerDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (event.button > 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    held.current = true;
    origin.current = { x: event.clientX, y: event.clientY };
    setHolding(true);
    setCancelArmed(false);
    setHint(null);
    const askedAt = Date.now();
    void latest.current.start().then((started) => {
      if (!started) return;
      if (!held.current) {
        // Released while the microphone was starting, e.g. during the permission prompt.
        latest.current.cancel();
        setHint(Date.now() - askedAt > MIN_HOLD_MS ? 'holdAgain' : 'hold');
        return;
      }
      startedAt.current = Date.now();
      if (typeof navigator !== 'undefined') navigator.vibrate?.(10);
    });
  }, []);

  const onPointerMove = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (!held.current) return;
    const dx = event.clientX - origin.current.x;
    const dy = event.clientY - origin.current.y;
    setCancelArmed(Math.hypot(dx, dy) > CANCEL_DISTANCE_PX);
  }, []);

  const finish = useCallback(
    (cancel: boolean) => {
      if (!held.current) return;
      held.current = false;
      setHolding(false);
      setCancelArmed(false);
      if (!startedAt.current) return; // still starting: handled when start resolves
      const heldFor = Date.now() - startedAt.current;
      startedAt.current = 0;
      if (cancel || cancelArmed || heldFor < MIN_HOLD_MS) {
        latest.current.cancel();
        if (!cancel && !cancelArmed) setHint('hold');
        return;
      }
      latest.current.stop();
    },
    [cancelArmed],
  );

  return {
    holding,
    cancelArmed,
    hint,
    clearHint: () => setHint(null),
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: () => finish(false),
      onPointerCancel: () => finish(true),
      // Long press must not open the iOS callout or the Android context menu.
      onContextMenu: (event: { preventDefault: () => void }) => event.preventDefault(),
    },
  };
}
